export interface ICinemaPhase {
  threshold: number;
  label: string;
  expectedSec: number;
}

// The last step is the world dressing itself, not a formality before the button: the stream is up there, and
// UE is still placing the first runtime objects behind it. It ends when UE says so (see WORLD_SETTLE_MS), so
// its `expectedSec` is an estimate of that, and the ramp creeps toward the end of the bar while it lasts.
export const CINEMA_PHASES: ICinemaPhase[] = [
  { threshold: 0, label: 'Reserving GPU slot', expectedSec: 60 },
  { threshold: 18, label: 'Starting the engine', expectedSec: 4 },
  { threshold: 45, label: 'Polishing the visuals', expectedSec: 4 },
  { threshold: 72, label: 'Connecting your stream', expectedSec: 5 },
  { threshold: 96, label: 'Placing the final objects', expectedSec: 10 },
];

// The same five steps a reconnect actually walks, said as a reconnect. Thresholds and expected seconds are
// deliberately identical to CINEMA_PHASES — the progress ramp and the segmented bar are driven off that one
// list, and only the words differ.
export const RECONNECT_PHASES: ICinemaPhase[] = [
  { threshold: 0, label: 'Finding you a new GPU slot', expectedSec: 60 },
  { threshold: 18, label: 'Starting the engine', expectedSec: 4 },
  { threshold: 45, label: 'Polishing the visuals', expectedSec: 4 },
  { threshold: 72, label: 'Reconnecting your stream', expectedSec: 5 },
  { threshold: 96, label: 'Putting you back in', expectedSec: 10 },
];

export const PHASE_TAIL_SEC = CINEMA_PHASES.map((_, i) =>
  CINEMA_PHASES.slice(i).reduce((total, phase) => total + phase.expectedSec, 0),
);

export const ETA_GLIDE_TAU_SEC = 1.2;
export const ETA_FLOOR_SEC = 5;
export const ETA_IMMINENT_LABEL = 'ready any moment now';

export const SEGMENT_GAP_PX = 5;
export const TIP_ROTATE_MS = 6000;
export const TIP_FADE_MS = 350;
export const STEP_FADE_MS = 180;

export const MAX_FRAME_DELTA_SEC = 0.1;
export const CATCHUP_RATE_PER_SEC = 2.2;
export const CATCHUP_FLOOR_PER_SEC = 2;
export const MIN_PHASE_TAU_SEC = 4;

// The stream being up is not the world being up: the video track and the room are there while UE is still
// placing the first runtime objects, and an entry taken at that instant walks into a half-dressed scene.
// UE says when it is really in — CLIENT_READY, behind its own pawnReady/avatarLoaded/pawnGrounded gate (the
// same announcement the reconnect loader and the seat-move venue proof already wait on) — so the LOADING
// runs until then, plus this, for the finished scene to reach the encoder. Deliberately part of the one
// progress rather than a second wait after it: the user is watching one thing load, not two.
export const WORLD_SETTLE_MS = 3_000;
// CLIENT_READY is announced exactly once, to whoever is attached at that instant, and a build old enough
// not to send it at all is a build this page still has to let people into. So the word is waited for, not
// depended on — the same backstop the reconnect loader keeps (MytaStreamsProvider RECONNECT_SETTLE_MS).
export const WORLD_ANNOUNCE_WAIT_MS = 25_000;

export interface ICinemaTip {
  before?: string;
  strong: string;
  after?: string;
}

export const CINEMA_TIPS: ICinemaTip[] = [
  {
    before: 'Press ',
    strong: 'F',
    after: ' to toggle fullscreen once you’re inside',
  },
  {
    before: 'Use ',
    strong: 'headphones',
    after: ' — audio in the space is spatial',
  },
  {
    before: 'Your mic starts ',
    strong: 'muted',
    after: ' — hold V to talk',
  },
  {
    before: 'Press ',
    strong: 'M',
    after: ' to open the map from anywhere',
  },
  {
    before: 'Click a teammate’s ',
    strong: 'avatar',
    after: ' to start a private voice chat',
  },
  {
    strong: 'Share your screen',
    after: ' to any media wall from the toolbar',
  },
  {
    before: 'Invite teammates with a ',
    strong: 'single link',
    after: ' — nothing to install',
  },
  {
    before: 'Press ',
    strong: 'Esc',
    after: ' to tune video quality and controls',
  },
  {
    before: 'Lost? Press ',
    strong: 'H',
    after: ' to teleport back to the hub',
  },
];

export const CINEMA_FONTS_LINK_ID = 'myta-cinema-fonts';
export const CINEMA_FONTS_URL =
  'https://fonts.googleapis.com/css2?family=Space+Grotesk:wght@500;600;700&family=Inter:wght@400;500;600&family=JetBrains+Mono:wght@400;500;600&display=swap';

export const LOADING_HEADLINE = 'Loading your experience';
export const READY_HEADLINE = 'Your experience is ready';
export const KEEP_TAB_HINT =
  'Keep this tab open — you’ll be able to enter the moment everything is ready.';
export const READY_HINT =
  'Your spot is reserved — glance at the controls, then step in whenever you’re ready.';
// The last step's name once it is over. The phase list carries what is HAPPENING, which on the final step
// is the world dressing itself; this is the one line that belongs to a loading that has finished.
export const READY_STEP = 'Ready to enter';
// A reconnect is not an entry: the user already pressed Enter once, the session is still theirs, and the
// page puts them back without asking again — so the words promise a return, never a decision.
export const RECONNECT_HEADLINE = 'Reconnecting you to the experience';
export const RECONNECT_READY_HEADLINE = 'Putting you back in';
export const RECONNECT_HINT =
  'The connection to your streaming machine dropped — keep this tab open and we’ll put you straight back where you were.';
export const RECONNECT_READY_HINT =
  'Almost there — you’ll be back in the experience in a moment, no need to click anything.';
// Shown while the entry is being placed on a SPOT box. Says the trade honestly and in that order — the saving
// first, because that is why the machine was chosen, then the cost of it, because an unexplained extra minute
// on a loading screen is read as a hang. Deliberately silent about interruption: if the box is ever actually
// reclaimed, SpotNoticeBanner says so at the moment it happens, and pre-warning every user about something
// that usually does not occur would trade a small wait for a large worry.
export const SPOT_NOTICE_TAG = 'Economy GPU';
export const SPOT_NOTICE_TEXT = {
  before: 'You’re being placed on a ',
  strong: 'discounted spot machine',
  after:
    ' — it costs a fraction of a regular one, so it can take a little longer to start.',
};

export const CONTROLS_KICKER = 'Controls';
export const CONTROLS_MOVE_LABEL = 'Move';
export const CONTROLS_LOOK_LABEL = 'Hold to look around';
export const CONTROLS_ROTATE_LABEL = 'Rotate camera';
